'use client';

import MobileDrawer from '@c/drawers/MobileDrawer';
import CurrencySymbol from '@c/forms/controls/assets/currency-symbol.svg';
import FormLabel from '@c/forms/controls/FormLabel';
import CurrencyInput from '@ui/Input/masked/CurrencyInput';
import SafeImage from '@ui/SafeImage';
import { getNumber } from '@util/createListingHelpers';
import { formatCurrency } from '@util/index';
import { ProductDocument } from 'models/product';
import Image, { StaticImageData } from 'next/image';
import React, { useState } from 'react';

export const PromotePricingDrawer = ({
  open,
  dismiss,
  product,
  icon,
  heading,
  description,
  inputLabel,
  percentageOptions,
  defaultPercentage = null,
  value,
  errorMessage,
  loading,
  onChangeValue,
  onSubmit,
  children,
}: {
  open: boolean;
  dismiss: () => void;
  product: ProductDocument;
  icon: StaticImageData;
  heading: string;
  description: string;
  inputLabel: string;
  percentageOptions: number[];
  defaultPercentage?: number | null;
  value: number | undefined;
  errorMessage?: string;
  loading: boolean;
  onChangeValue: (value: number | undefined, fromPreset: boolean) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  children?: React.ReactNode;
}) => {
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(
    defaultPercentage
  );

  const getDiscountedPrice = (percentage: number) =>
    Number((product.price * (1 - percentage / 100)).toFixed(2));

  return (
    <MobileDrawer show={open} dismiss={dismiss} title="Promote">
      <form className="flex flex-col" onSubmit={onSubmit}>
        <div className="flex flex-col gap-10 p-8">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Image src={icon} alt={heading} width={36} height={36} />
              <span className="text-heading-h5 font-bold tracking-[-0.01em]">
                {heading}
              </span>
            </div>
            <p className="text-body-md font-medium text-[#444]">
              {description}
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex w-full items-center gap-5 rounded-[16px] bg-[#f6f6f6] p-4">
              <SafeImage
                src={product.thumbnail}
                alt={product.title}
                width={200}
                height={200}
                className="size-[56px] rounded-[8px] object-cover"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1 text-body-md">
                <span className="truncate font-semibold">{product.title}</span>
                <span className="font-medium text-[#444]">
                  Current Price: {formatCurrency(product.price)}
                </span>
              </div>
            </div>

            <div className="flex w-full items-center gap-4">
              {percentageOptions.map((percentage) => {
                const selected = selectedPercentage === percentage;
                return (
                  <button
                    key={percentage}
                    type="button"
                    onClick={() => {
                      setSelectedPercentage(percentage);
                      onChangeValue(getDiscountedPrice(percentage), true);
                    }}
                    className={`flex flex-1 flex-col items-center justify-center rounded-[16px] border p-6 ${
                      selected
                        ? 'border-brand-secondary bg-[#f9e8e8]'
                        : 'border-[#d9d9d9] bg-white'
                    }`}
                  >
                    <span className="text-heading-h6 font-bold tracking-[-0.01em]">
                      {formatCurrency(getDiscountedPrice(percentage))}
                    </span>
                    <span className="text-body-sm font-semibold text-brand-secondary">
                      {percentage}% OFF
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-6">
              <div className="h-px flex-1 bg-[#d9d9d9]" />
              <span className="text-body-md font-semibold">or</span>
              <div className="h-px flex-1 bg-[#d9d9d9]" />
            </div>

            <FormLabel value={inputLabel} required errorMessage={errorMessage}>
              <div
                className={`flex h-[44px] w-full items-center gap-3 rounded-[12px] bg-[#f1f1f1] px-6 ${
                  errorMessage ? 'outline outline-2 outline-brand-red' : ''
                }`}
              >
                <Image
                  src={CurrencySymbol}
                  className="h-10 w-10"
                  alt="currency symbol"
                />
                <CurrencyInput
                  maskOptions={{
                    decimalLimit: 2,
                    integerLimit: 7,
                    decimalSymbol: '.',
                    allowDecimal: true,
                  }}
                  placeholder="0.00"
                  className="text-[2rem] font-medium outline-none"
                  value={value?.toString() ?? ''}
                  onChange={(e) => {
                    setSelectedPercentage(null);
                    onChangeValue(
                      e.target.value !== ''
                        ? getNumber(e.target.value)
                        : undefined,
                      false
                    );
                  }}
                />
              </div>
            </FormLabel>

            {children}
          </div>
        </div>

        <div className="flex flex-col gap-6 border-t border-[#f1f1f1] p-8">
          <button
            type="submit"
            disabled={loading}
            className="h-[44px] w-full rounded-full bg-brand-secondary text-body-md font-semibold text-white transition-colors hover:bg-brand-secondary-darker disabled:opacity-60"
          >
            {loading ? 'Loading...' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="h-[44px] w-full rounded-full border border-[#7f7f7f] text-body-md font-semibold text-black"
          >
            Cancel
          </button>
        </div>
      </form>
    </MobileDrawer>
  );
};
